import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { recordingEntitties } from '../../constant';
import sessionPage from '../../assets/images/session-page.png';
import Table from '../Table';
import './features-content.scss';

const FEATURE_CATEGORIES = [
	{
		path: 'recording',
		titleKey: 'recording_options',
		descKey: 'recording_options_profile',
	},
	{
		path: 'lockdown',
		titleKey: 'lockdown_options',
		descKey: 'lockdown_options_profile',
	},
	{
		path: 'verification',
		titleKey: 'verification_options',
		descKey: 'verification_options_profile',
	},
	{
		path: 'system',
		titleKey: 'system_options',
		descKey: 'system_options_profile',
	},
	{
		path: 'ai',
		titleKey: 'ai_features',
		descKey: 'artificial_intelligence_options',
	},
	{
		path: 'quiz-tools',
		titleKey: 'quiz_tools',
		descKey: 'quiz_tools_profile',
	},
];

const FeaturesContent = () => {
	const { t } = useTranslation();
	const allEntities = recordingEntitties.flatMap((entity) => entity.entities);
	const learnMoreLabel = t('learn_more', { defaultValue: 'Learn more' });

	const columns = [
		{
			heading: `${t('name')}`,
			accessor: 'name',
			width: '18%',
			align: 'left',
			Cell: ({ row }) => t(row.name),
		},
		{
			heading: `${t('counter_dependency')}`,
			accessor: 'counter_dependency',
			width: '18%',
			align: 'left',
			Cell: ({ row }) =>
				row.counter_dependency?.length > 0
					? row.counter_dependency.map((item) => t(item)).join(', ')
					: '-',
		},
		{
			heading: `${t('dependency')}`,
			accessor: 'dependency',
			width: '20%',
			align: 'left',
			Cell: ({ row }) =>
				row.dependency?.filter(Boolean)?.length > 0
					? row.dependency.filter(Boolean).map((item) => t(item)).join(', ')
					: '-',
		},
		{
			heading: `${t('description')}`,
			accessor: 'description',
			width: '44%',
			align: 'left',
			Cell: ({ row }) => t(row.description),
		},
	];

	return (
		<div className="features-page">
			<div className="features-page__container">
				<header className="features-page__header">
					<h1 className="features-page__title">{t('features')}</h1>
					<p className="features-page__intro">{t('mereos_policy_desc')}</p>
				</header>

				<div className="features-page__grid">
					{FEATURE_CATEGORIES.map((feature) => (
						<Link
							key={feature.path}
							to={`/features/${feature.path}`}
							className="features-page__card"
						>
							<h3 className="features-page__card-title">{t(feature.titleKey)}</h3>
							<p className="features-page__card-desc">{t(feature.descKey)}</p>
							<span className="features-page__card-link">
								{learnMoreLabel}
								<ArrowRight size={16} aria-hidden="true" />
							</span>
						</Link>
					))}
				</div>

				<section className="features-page__panel">
					<h2 className="features-page__panel-title">{t('mereos_test_configuration_advance_guide')}</h2>
					<p>{t('mereos_empowers_test_administrators')}</p>
				</section>

				<section className="features-page__panel">
					<h2 className="features-page__panel-title">{t('customizable_proctoring_features')}</h2>
					<p>{t('mereos_settings_enable_administrators')}</p>
					<ul className="features-page__numeric-list">
						<li>
							<span>{t('recording_options')}</span> {t('capture_audio_video')}
						</li>
						<li>
							<span>{t('lockdown_options')}</span> {t('restrict_or_detect')}
						</li>
						<li>
							<span>{t('verification_options')}</span> {t('confirm_the_test_takers')}
						</li>
						<li>
							<span>{t('ai_features')}: </span>
							{t('artificial_intelligence_overview_desc')}
						</li>
					</ul>
					<p>{t('the_system_modular_design_allows')}</p>
				</section>

				<section className="features-page__panel">
					<h2 className="features-page__panel-title">{t('enhanced_security_for_high_stakes_exams')}</h2>
					<p>
						{t('for_assessments_requiring')} {t('test_administrators_can')}:
					</p>
					<ul>
						<li>{t('activate_all_available_features')}</li>
						<li>{t('ensure_rigorous_monitoring')}</li>
						<li>{t('configure_customizable_thresholds')}</li>
					</ul>
				</section>

				<section className="features-page__panel">
					<h2 className="features-page__panel-title">{t('streamlined_decision_making')}</h2>
					<p>{t('test_administrators_retain_ultimate')}</p>
					<ul>
						<li>{t('selecting_appropriate_security')}</li>
					</ul>
					<p>{t('mereos_proctors_retain_ultimate_responsibility')}</p>
					<ul>
						<li>{t('monitoring_in_real_time_or_reviewing')}</li>
					</ul>
					<p>{t('mereos_intuitive_design_and_actionable')}</p>
				</section>

				<section className="features-page__panel">
					<h2 className="features-page__panel-title">{t('mereos_admin_panel_overview')}</h2>
					<p>{t('mereos_admin_panel_overview_desc')}</p>
					<h2 className="features-page__panel-title">{t('suspicion_indicators')}</h2>
					<p>{t('suspicion_indicators_desc')}</p>
					<ul>
						<li>{t('high_suspicion_scores')}</li>
						<li>{t('low_suspicion_scores')}</li>
					</ul>
					<p>{t('low_suspicion_scores_desc')}</p>
				</section>

				<section className="features-page__panel">
					<h2 className="features-page__panel-title">{t('detailed_exam_review')}</h2>
					<p>{t('proctors_can_click_on_a_test')}</p>
					<p>{t('key_features_include')}</p>
					<ul>
						<li>
							<span>{t('video_log')}:</span> {t('video_log_desc')}
						</li>
						<li>
							<span>{t('annotations')}:</span> {t('annotations_desc')}
						</li>
						<li>
							<span>{t('proctor_review')}</span> {t('proctor_review_desc')}
						</li>
					</ul>
					<div className="features-page__session-image">
						<img src={sessionPage} alt="" />
					</div>
					<h2 className="features-page__panel-title">{t('live_monitoring_option')}</h2>
					<p>{t('live_monitoring_option_desc')}</p>
				</section>

				<section className="features-page__panel">
					<h2 className="features-page__panel-title">{t('entity_relations')}</h2>
					<p>{t('entity_relations_desc')}</p>
					<Table
						column={columns}
						data={allEntities}
						showPagination={false}
						onSortedList={() => null}
						variant="modern"
					/>
				</section>
			</div>
		</div>
	);
};

export default FeaturesContent;
